import { createApi } from '@reduxjs/toolkit/query/react';

import {
  decodeAddedSeat,
  decodeCode,
  decodeCohort,
  decodeList,
  decodeBatchMoveResult,
  decodeMembershipResult,
  decodeMintedCode,
  decodeMovedSeat,
  decodeMoveDestinationGroup,
  decodeMatchedInstitution,
  decodeOperatorContextRead,
  decodeOperatorCoverageList,
  decodeOperatorRow,
  decodeRenamedCohort,
  decodeRevokedSeat,
  decodeRoster,
  decodeSeat,
  encodeAddRosterSeat,
  encodeBatchMoveSeats,
  encodeCohortMemberships,
  encodeAttachCohortCoach,
  encodeCreateCohort,
  encodeInstitutionIdsFilter,
  encodeMintCode,
  encodeMoveSeat,
  encodeOperatorFleetQuery,
  encodeSeatIdFilter,
  encodeRenameCohort,
  isKnownCapMode,
  isLandedUnreadable,
  INSTITUTION_ADMIN_ROUTE,
  REQUEST_PARAM,
  RESPONSE_HEADER,
  WRITE_UNREADABLE,
} from './institutionAdmin.contract';
import type {
  AddCohortMembershipsArgs,
  AddedSeat,
  AddRosterSeatArgs,
  AdminCode,
  AdminCohort,
  AdminRoster,
  AdminRostersArgs,
  CohortCoachArgs,
  BatchMoveResult,
  BatchMoveSeatsArgs,
  BatchSeatResult,
  ContextualList,
  CreateCohortArgs,
  MintCodeArgs,
  MintedCode,
  MoveDestinationGroup,
  MoveDestinationsArgs,
  MovedSeat,
  MoveSeatArgs,
  OperatorContextRead,
  OperatorFleetArgs,
  OperatorFleetPage,
  RemoveCohortMembershipsArgs,
  RenameCohortArgs,
  RenamedCohort,
  RevokedSeat,
  RosterScopedArgs,
  RevokeCodeArgs,
  SeatActionArgs,
  SeatDetail,
  SeatDetailArgs,
} from './institutionAdmin.types';
import { baseQueryWithAuth } from '../baseQuery';
import { coachingDashboardApi } from '../coachingDashboard/coachingDashboard.api';

const responseHeaders = (meta: unknown): Headers | undefined =>
  (meta as { response?: Response } | undefined)?.response?.headers;

const parseIntHeader = (meta: unknown, name: string): number | null => {
  const raw = responseHeaders(meta)?.get(name);

  if (!raw) return null;

  const parsed = parseInt(raw, 10);

  return Number.isNaN(parsed) ? null : parsed;
};

const buildQuery = (institutionIds?: readonly number[]): string => {
  if (!institutionIds || institutionIds.length === 0) return '';

  const params = new URLSearchParams();

  params.append(REQUEST_PARAM.filter, encodeInstitutionIdsFilter(institutionIds));

  return `?${params.toString()}`;
};

const reportUnknownCapModes = (route: string, rows: unknown) => {
  if (!Array.isArray(rows)) return;

  const unknown = Array.from(
    new Set(
      rows
        .map((row) => (row as { cap_mode?: unknown })?.cap_mode)
        .filter((mode) => mode !== undefined && !isKnownCapMode(mode)),
    ),
  );

  if (unknown.length > 0) {
    console.error(
      `Unrecognised cap_mode ${unknown.map((m) => JSON.stringify(m)).join(', ')} from ${route}; reading as 'hard'`,
    );
  }
};

/**
 * What an operator row lost on the way here. The decoder keeps the row either way, so
 * without this the loss is invisible: an unreadable set of lines renders as an operator
 * with none, and a dropped match is a school the search reached and the list cannot offer.
 */
const reportUnreadableOperatorParts = (route: string, rows: unknown) => {
  if (!Array.isArray(rows)) return;

  rows.forEach((row) => {
    const raw = row as { id?: unknown; coverage?: unknown; matched_institutions?: unknown };
    const matched = decodeList(raw?.matched_institutions, decodeMatchedInstitution);
    const lost: string[] = [];

    if (decodeOperatorCoverageList(raw?.coverage) === null) lost.push('its capacity lines');
    if (matched === null) lost.push('its matched institutions');
    else if (matched.dropped > 0) lost.push(`${matched.dropped} matched institution(s)`);

    if (lost.length > 0) {
      console.error(`Operator ${String(raw?.id)} lost ${lost.join(' and ')} from ${route}`);
    }
  });
};

/**
 * The count and the list are two projections of one attachment set, so a row where they
 * disagree has lost coaches on the way here. The display renders the list, so without
 * this the loss shows as a shorter row and nothing else.
 */
const reportCoachDisagreement = (route: string, rows: readonly AdminCohort[]) => {
  const disagreeing = rows.filter((row) => row.coaches.length !== row.coachCount);

  if (disagreeing.length > 0) {
    console.error(
      `Cohort ${disagreeing.map((row) => row.id).join(', ')} counted a different number of coaches than listed from ${route}`,
    );
  }
};

/**
 * The coaching dashboard reads its rows from a DIFFERENT api slice, so this slice's
 * `invalidatesTags` cannot reach them — RTK resolves tags within one api's registry.
 * Without this, a client-side navigation after an attach or detach reuses the dashboard's
 * cached rows and shows coverage that no longer exists until a hard reload.
 *
 * The bare types are deliberate: a general tag matches every entry of that type, so the
 * per-member reads are covered along with the two lists.
 */
const COVERAGE_TAGS = [
  { type: 'CoachingDashboardInstitution' as const },
  { type: 'CoachingDashboardAssignees' as const },
];

const invalidateDerivedCoverage = (dispatch: (action: unknown) => unknown) => {
  dispatch(coachingDashboardApi.util.invalidateTags(COVERAGE_TAGS));
};

/**
 * The batch move's settle handler, and deliberately unconditional. Every OTHER door here
 * commits in one transaction, so a rejection proves nothing landed and the caches are
 * still true. This one runs each seat in its own transaction, so a rejection proves
 * nothing at all — items before the failure may have moved. Reading a refusal as
 * "no change" is the assumption that leaves the dashboard showing seats on rosters they
 * have already left.
 */
export const invalidateCoverageWhatever = async (
  _args: unknown,
  {
    dispatch,
    queryFulfilled,
  }: { dispatch: (action: unknown) => unknown; queryFulfilled: Promise<unknown> },
) => {
  try {
    await queryFulfilled;
  } catch {
    // Deliberately swallowed: the surface reports the outcome, and the caches move either way.
  }

  dispatch(institutionAdminApi.util.invalidateTags(SEAT_WRITE_TAGS));
  invalidateDerivedCoverage(dispatch);
};

/**
 * The settle handler for every seat door EXCEPT the batch move. A refused write changed nothing; one that COMMITTED
 * and could not be read back changed everything a refused one would have, and rejects all
 * the same — so returning early there leaves every cache serving rows the write already
 * replaced. `invalidatesTags` does not fire on a rejection either, which is why the landed
 * arm dispatches its own.
 */
export const invalidateCoverageOnSettled = async (
  _args: unknown,
  {
    dispatch,
    queryFulfilled,
  }: { dispatch: (action: unknown) => unknown; queryFulfilled: Promise<unknown> },
) => {
  try {
    await queryFulfilled;
  } catch (error) {
    if (!isLandedUnreadable((error as { error?: unknown })?.error)) return;

    dispatch(institutionAdminApi.util.invalidateTags(SEAT_WRITE_TAGS));
  }

  invalidateDerivedCoverage(dispatch);
};

/**
 * What a seat revoke moves. The SEAT list is roster-bound, because a seat lives on one
 * roster. The COHORTS tag carries no id, because the cascade does not: an attachment's
 * authorizing seat is resolved institution-wide, so what this revoke detaches can sit on
 * a different roster than the seat it ended. Narrowing that tag would derive by roster in
 * the cache — the exact move the consequence reads a field to avoid.
 */
export const revokeSeatTags = (rosterId: number) => [
  { type: 'InstitutionAdminSeats' as const, id: rosterId },
  { type: 'InstitutionAdminCohorts' as const },
  { type: 'InstitutionAdminRosters' as const },
  { type: 'InstitutionAdminOperators' as const },
];

/**
 * Everything a seat write can move. Used only where the write landed and its answer was
 * lost: what changed is then unknown, so nothing that could have changed is kept.
 */
const SEAT_WRITE_TAGS = [
  { type: 'InstitutionAdminSeats' as const },
  { type: 'InstitutionAdminCohorts' as const },
  { type: 'InstitutionAdminRosters' as const },
  { type: 'InstitutionAdminOperators' as const },
];

const shapeError = (route: string, body: unknown, reason: string) => ({
  error: {
    status: 'CUSTOM_ERROR' as const,
    data: body,
    error: `${reason} from ${route}`,
  },
});

/**
 * A 2xx is post-commit, so the write landed whether or not anything readable came
 * back. Two channels reach that state: a body that parses but will not decode,
 * and one that never parses at all — the second arrives as an error, so it would
 * otherwise short-circuit to the never-happened path.
 */
/**
 * A mint row's `value` is a live join credential. The diagnostic keeps the shape
 * that made the row undecodable and drops the one field that must never reach a
 * log line or a persisted error payload.
 */
const withoutCodeValue = (body: unknown): unknown => {
  if (Array.isArray(body)) return body.map(withoutCodeValue);

  if (typeof body !== 'object' || body === null) return body;

  return Object.fromEntries(
    Object.entries(body as Record<string, unknown>).map(([key, value]) =>
      key === 'value' ? [key, '[redacted]'] : [key, withoutCodeValue(value)],
    ),
  );
};

/**
 * A parse failure carries the raw response text, which on the mint door is a
 * credential in a string the redactor cannot see into. Only the shape that
 * classified it is safe to keep.
 */
const parseFailureShape = (error: unknown) => {
  const shaped = error as { status?: unknown; originalStatus?: unknown };

  return { status: shaped.status, originalStatus: shaped.originalStatus };
};

const isPostCommit = (error: unknown): boolean => {
  const shaped = error as { status?: unknown; originalStatus?: unknown } | null | undefined;

  if (shaped?.status !== 'PARSING_ERROR') return false;

  const status = shaped.originalStatus;

  return typeof status === 'number' && status >= 200 && status < 300;
};

/**
 * Only reachable past a 2xx, so the write itself succeeded and only the readback
 * failed. Carries the discriminator that keeps it off the never-happened path.
 */
const undecodableWrite = (route: string, body: unknown) => {
  const message = `Could not read the written row from ${route}`;
  const safe = withoutCodeValue(body);

  console.error(message, safe);

  return {
    error: {
      status: 'CUSTOM_ERROR' as const,
      data: { code: WRITE_UNREADABLE, body: safe },
      error: message,
    },
  };
};

/**
 * A door that reads nothing back still has one anomaly worth a trace: a 2xx whose body would
 * not parse. The state is right either way, so this only keeps the case distinguishable from
 * a server no-op when someone is looking for why a write appeared to change nothing.
 */
const reportUnreadableSuccess = (route: string, error: unknown) => {
  console.error(`Unreadable 2xx from ${route}`, parseFailureShape(error));
};

/**
 * null when nothing readable came back — the body was not a list, or every row it carried
 * failed to decode
 */
const contextualList = <T>(
  route: string,
  rows: unknown,
  meta: unknown,
  decode: (row: unknown) => T | null,
): ContextualList<T> | null => {
  const decoded = decodeList(rows, decode);

  if (decoded === null) return null;

  const { rows: data, dropped } = decoded;

  if (dropped > 0) {
    console.error(`Dropped ${dropped} unreadable row(s) from ${route}`);
  }
  if (dropped > 0 && data.length === 0) return null;

  return {
    data,
    total: parseIntHeader(meta, RESPONSE_HEADER.totalCount) ?? data.length,
    operatorInstitutionId: parseIntHeader(meta, RESPONSE_HEADER.operatorInstitutionId),
    dropped,
  };
};

export const institutionAdminApi = createApi({
  reducerPath: 'institutionAdminApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: [
    'InstitutionAdminRosters',
    'InstitutionAdminCohorts',
    'InstitutionAdminCodes',
    'InstitutionAdminSeats',
    'InstitutionAdminOperators',
  ],
  endpoints: (build) => ({
    getAdminRosters: build.query<ContextualList<AdminRoster>, AdminRostersArgs>({
      queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
        const route = INSTITUTION_ADMIN_ROUTE.rosters;
        const result = await baseQuery(`${route}${buildQuery(args.institutionIds)}`);

        if (result.error) return { error: result.error };

        const list = contextualList(route, result.data, result.meta, decodeRoster);

        reportUnknownCapModes(route, result.data);

        return list === null
          ? shapeError(route, result.data, 'Could not read any roster rows')
          : { data: list };
      },
      providesTags: [{ type: 'InstitutionAdminRosters' }],
    }),
    getOperatorFleet: build.query<OperatorFleetPage, OperatorFleetArgs>({
      queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
        const route = INSTITUTION_ADMIN_ROUTE.operators;
        const result = await baseQuery(`${route}?${encodeOperatorFleetQuery(args)}`);

        if (result.error) return { error: result.error };

        const decoded = decodeList(result.data, decodeOperatorRow);

        reportUnreadableOperatorParts(route, result.data);

        if (decoded === null) {
          return shapeError(route, result.data, 'Could not read any operator rows');
        }

        if (decoded.dropped > 0) {
          console.error(`Dropped ${decoded.dropped} unreadable row(s) from ${route}`);
        }

        // Every row unreadable is a page that answered nothing, not an empty page: a search
        // that matched nothing is the only empty list this read has.
        if (decoded.dropped > 0 && decoded.rows.length === 0) {
          return shapeError(route, result.data, 'Could not read any operator rows');
        }

        const total = parseIntHeader(result.meta, RESPONSE_HEADER.totalCount);

        if (total === null) {
          console.error(`No usable ${RESPONSE_HEADER.totalCount} from ${route}`);
        }

        return {
          data: { data: decoded.rows, total, dropped: decoded.dropped },
        };
      },
      // The row is a rollup over seat state, so every seat write stales it; the tag is what
      // carries that, since the write happens under one operator and the row is one of many.
      providesTags: [{ type: 'InstitutionAdminOperators' }],
    }),
    getOperatorContext: build.query<OperatorContextRead, undefined>({
      queryFn: async (_args, _queryApi, _extraOptions, baseQuery) => {
        const route = INSTITUTION_ADMIN_ROUTE.operatorContext;
        const result = await baseQuery(route);

        if (result.error) return { error: result.error };

        const decoded = decodeOperatorContextRead(result.data);

        return decoded === null
          ? shapeError(route, result.data, 'Could not read the operator context')
          : { data: decoded };
      },
    }),
    getRosterCohorts: build.query<ContextualList<AdminCohort>, RosterScopedArgs>({
      queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
        const route = INSTITUTION_ADMIN_ROUTE.cohorts(args.rosterId);
        const result = await baseQuery(route);

        if (result.error) return { error: result.error };

        const list = contextualList(route, result.data, result.meta, decodeCohort);

        if (list !== null) reportCoachDisagreement(route, list.data);

        return list === null
          ? shapeError(route, result.data, 'Could not read any cohort rows')
          : { data: list };
      },
      providesTags: (_result, _error, { rosterId }) => [
        { type: 'InstitutionAdminCohorts', id: rosterId },
      ],
    }),
    getRosterCodes: build.query<ContextualList<AdminCode>, RosterScopedArgs>({
      queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
        const route = INSTITUTION_ADMIN_ROUTE.codes(args.rosterId);
        const result = await baseQuery(route);

        if (result.error) return { error: result.error };

        const list = contextualList(route, result.data, result.meta, decodeCode);

        return list === null
          ? shapeError(route, result.data, 'Could not read any code rows')
          : { data: list };
      },
      providesTags: (_result, _error, { rosterId }) => [
        { type: 'InstitutionAdminCodes', id: rosterId },
      ],
    }),
    createCohort: build.mutation<AdminCohort, CreateCohortArgs>({
      queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
        const route = INSTITUTION_ADMIN_ROUTE.cohorts(args.rosterId);
        const result = await baseQuery({
          url: route,
          method: 'POST',
          body: encodeCreateCohort(args),
        });

        if (result.error) {
          return isPostCommit(result.error)
            ? undecodableWrite(route, parseFailureShape(result.error))
            : { error: result.error };
        }

        const cohort = decodeCohort(result.data);

        return cohort === null ? undecodableWrite(route, result.data) : { data: cohort };
      },
      onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
        } catch {
          return;
        }

        invalidateDerivedCoverage(dispatch);
      },
      // A create can also yield a code, so the code list is stale too.
      invalidatesTags: (_result, _error, { rosterId }) => [
        { type: 'InstitutionAdminCohorts', id: rosterId },
        { type: 'InstitutionAdminCodes', id: rosterId },
      ],
    }),
    attachCohortCoach: build.mutation<null, CohortCoachArgs>({
      queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
        const route = INSTITUTION_ADMIN_ROUTE.cohortCoaches(args.rosterId, args.cohortId);
        const result = await baseQuery({
          url: route,
          method: 'POST',
          body: encodeAttachCohortCoach(args),
        });

        // The attachment row it answers with is never read: the cohorts read is what both
        // the row and the dialog render from, so an unparsable 2xx costs this door nothing.
        if (result.error) {
          if (!isPostCommit(result.error)) return { error: result.error };

          reportUnreadableSuccess(route, result.error);
        }

        return { data: null };
      },
      onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
        // A refused write changes no coverage, and an uncaught rejection here surfaces as an
        // unhandled promise rejection rather than as the refusal the surface already handles.
        try {
          await queryFulfilled;
        } catch {
          return;
        }

        invalidateDerivedCoverage(dispatch);
      },
      invalidatesTags: (_result, _error, { rosterId }) => [
        { type: 'InstitutionAdminCohorts', id: rosterId },
      ],
    }),
    detachCohortCoach: build.mutation<null, CohortCoachArgs>({
      queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
        const route = INSTITUTION_ADMIN_ROUTE.cohortCoach(
          args.rosterId,
          args.cohortId,
          args.coachUserId,
        );
        const result = await baseQuery({ url: route, method: 'DELETE' });

        if (result.error) {
          if (!isPostCommit(result.error)) return { error: result.error };

          reportUnreadableSuccess(route, result.error);
        }

        return { data: null };
      },
      onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
        // A refused write changes no coverage, and an uncaught rejection here surfaces as an
        // unhandled promise rejection rather than as the refusal the surface already handles.
        try {
          await queryFulfilled;
        } catch {
          return;
        }

        invalidateDerivedCoverage(dispatch);
      },
      invalidatesTags: (_result, _error, { rosterId }) => [
        { type: 'InstitutionAdminCohorts', id: rosterId },
      ],
    }),
    renameCohort: build.mutation<RenamedCohort, RenameCohortArgs>({
      queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
        const route = INSTITUTION_ADMIN_ROUTE.cohort(args.rosterId, args.cohortId);
        const result = await baseQuery({
          url: route,
          method: 'PATCH',
          body: encodeRenameCohort(args),
        });

        if (result.error) {
          return isPostCommit(result.error)
            ? undecodableWrite(route, parseFailureShape(result.error))
            : { error: result.error };
        }

        const renamed = decodeRenamedCohort(result.data);

        return renamed === null ? undecodableWrite(route, result.data) : { data: renamed };
      },
      invalidatesTags: (_result, _error, { rosterId }) => [
        { type: 'InstitutionAdminCohorts', id: rosterId },
      ],
    }),
    mintCode: build.mutation<MintedCode, MintCodeArgs>({
      queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
        const route = INSTITUTION_ADMIN_ROUTE.codes(args.rosterId);
        const result = await baseQuery({
          url: route,
          method: 'POST',
          body: encodeMintCode(args),
        });

        if (result.error) {
          return isPostCommit(result.error)
            ? undecodableWrite(route, parseFailureShape(result.error))
            : { error: result.error };
        }

        const minted = decodeMintedCode(result.data);

        return minted === null ? undecodableWrite(route, result.data) : { data: minted };
      },
      // A pinned mint becomes the cohort's share code, so both lists are stale.
      invalidatesTags: (_result, _error, { rosterId }) => [
        { type: 'InstitutionAdminCodes', id: rosterId },
        { type: 'InstitutionAdminCohorts', id: rosterId },
      ],
    }),
    revokeCode: build.mutation<AdminCode, RevokeCodeArgs>({
      queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
        const route = INSTITUTION_ADMIN_ROUTE.revokeCode(args.rosterId, args.codeId);
        const result = await baseQuery({ url: route, method: 'POST' });

        if (result.error) {
          return isPostCommit(result.error)
            ? undecodableWrite(route, parseFailureShape(result.error))
            : { error: result.error };
        }

        const code = decodeCode(result.data);

        return code === null ? undecodableWrite(route, result.data) : { data: code };
      },
      // Revoking the code a cohort was sharing leaves the cohort row stale too.
      invalidatesTags: (_result, _error, { rosterId }) => [
        { type: 'InstitutionAdminCodes', id: rosterId },
        { type: 'InstitutionAdminCohorts', id: rosterId },
      ],
    }),
    getSeatDetail: build.query<SeatDetail, SeatDetailArgs>({
      queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
        const route = INSTITUTION_ADMIN_ROUTE.seats(args.rosterId);
        const params = new URLSearchParams({
          [REQUEST_PARAM.filter]: encodeSeatIdFilter(args.seatId),
        });
        const result = await baseQuery(`${route}?${params.toString()}`);

        // A REFUSED read is an error, not an answer. Returning `unknown` as data would
        // cache it as a SUCCESS, so the dialog stays on could-not-confirm for the rest of
        // the session with no retry — a transient failure made permanent.
        if (result.error) return { error: result.error };

        const decoded = decodeList(result.data, decodeSeat);

        if (decoded === null) {
          return shapeError(route, result.data, `Could not read seat ${args.seatId}`);
        }

        // Answered, but without this seat. That is a real answer — the read is narrowed to
        // one seat and the roster narrowing still binds — and it is still not the same as
        // a seat that holds nothing.
        const seat = decoded.rows.find((row) => row.id === args.seatId);

        return { data: seat === undefined ? { kind: 'unknown' } : { kind: 'known', seat } };
      },
      providesTags: (_result, _error, { rosterId }) => [
        { type: 'InstitutionAdminSeats', id: rosterId },
      ],
    }),
    getMoveDestinations: build.query<MoveDestinationGroup[], MoveDestinationsArgs>({
      queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
        const route = INSTITUTION_ADMIN_ROUTE.moveDestinations;
        // The key is omitted rather than sent empty: an empty seat_id is a malformed
        // seated request, not the seatless form.
        const query =
          args.seatId === null
            ? ''
            : `?${new URLSearchParams({ [REQUEST_PARAM.seatId]: String(args.seatId) })}`;
        const result = await baseQuery(`${route}${query}`);

        if (result.error) return { error: result.error };

        const decoded = decodeList(result.data, decodeMoveDestinationGroup);

        if (decoded === null) {
          return shapeError(route, result.data, 'Could not read any destination groups');
        }

        // A caller holding one root answers one group, and a seat whose every destination
        // is its own roster answers none — both are empty choosers the dialog must be able
        // to tell apart from a read that failed, so an empty list stays a success.
        if (decoded.dropped > 0) {
          console.error(`Dropped ${decoded.dropped} unreadable group(s) from ${route}`);
        }

        return { data: decoded.rows };
      },
      providesTags: (_result, _error, { seatId }) => [
        { type: 'InstitutionAdminSeats', id: `destinations-${seatId ?? 'all'}` },
      ],
    }),
    revokeSeat: build.mutation<RevokedSeat, SeatActionArgs>({
      queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
        const route = INSTITUTION_ADMIN_ROUTE.revokeSeat(args.rosterId, args.seatId);
        const result = await baseQuery({ url: route, method: 'POST' });

        if (result.error) {
          return isPostCommit(result.error)
            ? undecodableWrite(route, parseFailureShape(result.error))
            : { error: result.error };
        }

        const seat = decodeRevokedSeat(result.data);

        return seat === null ? undecodableWrite(route, result.data) : { data: seat };
      },
      onQueryStarted: invalidateCoverageOnSettled,
      invalidatesTags: (_result, _error, { rosterId }) => revokeSeatTags(rosterId),
    }),
    moveSeat: build.mutation<MovedSeat, MoveSeatArgs>({
      queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
        const route = INSTITUTION_ADMIN_ROUTE.moveSeat(args.rosterId, args.seatId);
        const result = await baseQuery({
          url: route,
          method: 'POST',
          body: encodeMoveSeat(args),
        });

        if (result.error) {
          return isPostCommit(result.error)
            ? undecodableWrite(route, parseFailureShape(result.error))
            : { error: result.error };
        }

        const moved = decodeMovedSeat(result.data);

        return moved === null ? undecodableWrite(route, result.data) : { data: moved };
      },
      onQueryStarted: invalidateCoverageOnSettled,
      // Both ends changed, and seats_used moved on each, so the rosters listing goes with
      // the two seat lists.
      invalidatesTags: (_result, _error, { rosterId, destinationRosterId }) => [
        { type: 'InstitutionAdminSeats', id: rosterId },
        { type: 'InstitutionAdminSeats', id: destinationRosterId },
        { type: 'InstitutionAdminCohorts', id: rosterId },
        { type: 'InstitutionAdminRosters' },
        { type: 'InstitutionAdminOperators' },
      ],
    }),
    addRosterSeat: build.mutation<AddedSeat, AddRosterSeatArgs>({
      queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
        const route = INSTITUTION_ADMIN_ROUTE.seats(args.rosterId);
        const result = await baseQuery({
          url: route,
          method: 'POST',
          body: encodeAddRosterSeat(args),
        });

        if (result.error) {
          return isPostCommit(result.error)
            ? undecodableWrite(route, parseFailureShape(result.error))
            : { error: result.error };
        }

        const added = decodeAddedSeat(result.data);

        return added === null ? undecodableWrite(route, result.data) : { data: added };
      },
      onQueryStarted: invalidateCoverageOnSettled,
      invalidatesTags: (_result, _error, { rosterId }) => [
        { type: 'InstitutionAdminSeats', id: rosterId },
        { type: 'InstitutionAdminRosters' },
        { type: 'InstitutionAdminOperators' },
      ],
    }),
    addCohortMemberships: build.mutation<BatchSeatResult[], AddCohortMembershipsArgs>({
      queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
        const route = INSTITUTION_ADMIN_ROUTE.cohortMemberships(args.rosterId, args.cohortId);
        const result = await baseQuery({
          url: route,
          method: 'POST',
          body: encodeCohortMemberships(args),
        });

        if (result.error) {
          return isPostCommit(result.error)
            ? undecodableWrite(route, parseFailureShape(result.error))
            : { error: result.error };
        }

        const decoded = decodeList(result.data, decodeMembershipResult);

        // Every item is reported back, so a short list has lost outcomes for ids that were
        // acted on. Reporting the survivors as the whole answer would show a batch as
        // smaller than the one that committed.
        if (
          decoded === null ||
          decoded.dropped > 0 ||
          decoded.rows.length !== args.rosterSeatIds.length
        ) {
          return undecodableWrite(route, result.data);
        }

        return { data: decoded.rows };
      },
      onQueryStarted: invalidateCoverageOnSettled,
      invalidatesTags: (_result, _error, { rosterId }) => [
        { type: 'InstitutionAdminSeats', id: rosterId },
        { type: 'InstitutionAdminCohorts', id: rosterId },
      ],
    }),
    removeCohortMemberships: build.mutation<BatchSeatResult[], RemoveCohortMembershipsArgs>({
      queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
        const route = INSTITUTION_ADMIN_ROUTE.removeCohortMemberships(args.rosterId, args.cohortId);
        const result = await baseQuery({
          url: route,
          method: 'POST',
          body: encodeCohortMemberships(args),
        });

        if (result.error) {
          return isPostCommit(result.error)
            ? undecodableWrite(route, parseFailureShape(result.error))
            : { error: result.error };
        }

        const decoded = decodeList(result.data, decodeMembershipResult);

        // Short reports are undecodable for the same reason as the add door's.
        if (
          decoded === null ||
          decoded.dropped > 0 ||
          decoded.rows.length !== args.rosterSeatIds.length
        ) {
          return undecodableWrite(route, result.data);
        }

        return { data: decoded.rows };
      },
      onQueryStarted: invalidateCoverageOnSettled,
      invalidatesTags: (_result, _error, { rosterId }) => [
        { type: 'InstitutionAdminSeats', id: rosterId },
        { type: 'InstitutionAdminCohorts', id: rosterId },
      ],
    }),
    batchMoveSeats: build.mutation<BatchMoveResult[], BatchMoveSeatsArgs>({
      queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
        const route = INSTITUTION_ADMIN_ROUTE.batchMoveSeats;
        const result = await baseQuery({
          url: route,
          method: 'POST',
          body: encodeBatchMoveSeats(args),
        });

        if (result.error) {
          return isPostCommit(result.error)
            ? undecodableWrite(route, parseFailureShape(result.error))
            : { error: result.error };
        }

        const decoded = decodeList(result.data, decodeBatchMoveResult);

        // Each item runs its own transaction, so a lost row is a move that may well have
        // landed. Reporting the survivors as the whole answer would leave it invisible.
        if (
          decoded === null ||
          decoded.dropped > 0 ||
          decoded.rows.length !== args.seatIds.length
        ) {
          return undecodableWrite(route, result.data);
        }

        return { data: decoded.rows };
      },
      onQueryStarted: invalidateCoverageWhatever,
      // The batch spans source rosters this args shape does not name, so every seat list
      // goes rather than the ones that can be identified from here. The fleet tag rides
      // `invalidateCoverageWhatever`, which fires for this door whatever the outcome.
      invalidatesTags: () => [
        { type: 'InstitutionAdminSeats' },
        { type: 'InstitutionAdminCohorts' },
        { type: 'InstitutionAdminRosters' },
      ],
    }),
  }),
});

export const {
  useAddCohortMembershipsMutation,
  useAddRosterSeatMutation,
  useAttachCohortCoachMutation,
  useBatchMoveSeatsMutation,
  useCreateCohortMutation,
  useDetachCohortCoachMutation,
  useGetAdminRostersQuery,
  useGetMoveDestinationsQuery,
  useGetOperatorContextQuery,
  useGetOperatorFleetQuery,
  useGetRosterCodesQuery,
  useGetRosterCohortsQuery,
  useGetSeatDetailQuery,
  useMintCodeMutation,
  useMoveSeatMutation,
  useRemoveCohortMembershipsMutation,
  useRenameCohortMutation,
  useRevokeCodeMutation,
  useRevokeSeatMutation,
} = institutionAdminApi;
